import { useCallback, useMemo, useRef } from 'react'
import {
  createFilterOptions,
  FilterOptionsState,
  Paper,
  PaperProps,
  Typography,
} from '@mui/material'
import { Organization } from '@/types/organizations'

// The Autocomplete listbox isn't virtualized, so every match becomes a
// ButtonBase in the DOM on each keystroke. Accounts with thousands of orgs
// stall without a cap, and a one-character search matches nearly all of them.
const OPTION_LIMIT = 100
const MIN_SEARCH_LENGTH = 2

const filterAll = createFilterOptions<Organization>()

/**
 * Holds the org dropdown back until the search is worth running, then caps how
 * many options it renders and says how many matches were left out.
 *
 * An account whose whole org list already fits inside the cap has nothing to
 * gate, so it stays browsable without typing.
 */
export default function useCappedOrgOptions(inputValue = '', orgCount = 0) {
  const hiddenMatchCount = useRef(0)
  const searchRequired = orgCount > OPTION_LIMIT
  const belowMinLength = inputValue.trim().length < MIN_SEARCH_LENGTH

  const filterOptions = useCallback(
    (options: Organization[], state: FilterOptionsState<Organization>) => {
      hiddenMatchCount.current = 0
      if (
        searchRequired &&
        state.inputValue.trim().length < MIN_SEARCH_LENGTH
      ) {
        return []
      }

      const matches = filterAll(options, state)
      hiddenMatchCount.current = Math.max(0, matches.length - OPTION_LIMIT)
      return matches.slice(0, OPTION_LIMIT)
    },
    [searchRequired],
  )

  const noOptionsText =
    searchRequired && belowMinLength
      ? `Type at least ${MIN_SEARCH_LENGTH} characters to search organizations`
      : 'No organizations match your search'

  // Must keep its identity — a new component type each render remounts the popup.
  // The footer sits in the Paper rather than the listbox so it stays visible
  // while the options scroll, and so it isn't counted as an option.
  const PaperComponent = useMemo(
    () =>
      function CappedOptionsPaper({ children, ...paperProps }: PaperProps) {
        const hidden = hiddenMatchCount.current
        return (
          <Paper {...paperProps}>
            {children}
            {hidden > 0 && (
              <Typography
                variant="caption"
                color="textSecondary"
                component="p"
                // Keep focus in the input if the note gets clicked.
                onMouseDown={(event) => event.preventDefault()}
                sx={{
                  px: 2,
                  py: 1,
                  borderTop: 1,
                  borderColor: 'divider',
                }}
              >
                {hidden.toLocaleString()} more{' '}
                {hidden === 1 ? 'match' : 'matches'} not shown. Keep typing to
                narrow the list.
              </Typography>
            )}
          </Paper>
        )
      },
    [],
  )

  return { filterOptions, PaperComponent, noOptionsText }
}
